"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

const componentRules = [
  {
    title: "Use Style Drivers",
    description: "All component styles should come from componentStyle.ts",
    do: "className={cn(styleDrivers.button.base, styleDrivers.button.variants.solid)}",
    dont: "className=\"bg-primary text-white px-4 py-2\"",
  },
  {
    title: "Follow the 4-Max Rule",
    description: "Each token type should have at most 4 options",
    do: "size: 'sm' | 'md' | 'lg' | 'xl'",
    dont: "size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'",
  },
  {
    title: "Prop Naming Conventions",
    description: "Use consistent naming for props across components",
    do: "isDisabled, isLoading, onChange, onSelect",
    dont: "disabled, loading, handleChange, selectHandler",
  },
  {
    title: "Semantic HTML First",
    description: "Use native HTML elements before reaching for ARIA",
    do: "<button onClick={...}>Submit</button>",
    dont: "<div role=\"button\" onClick={...}>Submit</div>",
  },
];

const fileStructure = [
  { name: "ComponentName.tsx", description: "Main component file" },
  { name: "ComponentName.types.ts", description: "TypeScript interfaces and types" },
  { name: "ComponentName.test.tsx", description: "Unit and integration tests" },
  { name: "README.md", description: "Usage documentation" },
];

const namingConventions = [
  { type: "Folders", convention: "kebab-case", example: "date-picker, filter-builder" },
  { type: "Files", convention: "kebab-case", example: "date-picker.tsx, date-picker.types.ts" },
  { type: "Components", convention: "PascalCase", example: "DatePicker, FilterBuilder" },
  { type: "Props", convention: "camelCase", example: "isDisabled, onChange, defaultValue" },
  { type: "CSS Variables", convention: "kebab-case", example: "--primary-color, --spacing-md" },
];

export default function DocsGuidelines() {
  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Component Guidelines
            </Text>
            <Badge variant="secondary">Standards</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            Standards and conventions for building consistent, maintainable components
            in the design system.
          </Text>
        </div>
      </Section>

      {/* Component Rules */}
      <Section title="Component Rules">
        <div className="space-y-4">
          {componentRules.map((rule) => (
            <SectionCard key={rule.title} contentClassName="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <Text as="p" className="font-semibold">{rule.title}</Text>
                  <Text as="p" className="text-sm text-muted-foreground">
                    {rule.description}
                  </Text>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2 p-3 bg-success-bg rounded-lg border border-success/20">
                    <Text as="p" className="text-xs text-success font-medium">Do</Text>
                    <code className="text-sm font-mono">{rule.do}</code>
                  </div>
                  <div className="flex flex-col gap-2 p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                    <Text as="p" className="text-xs text-destructive font-medium">Don&apos;t</Text>
                    <code className="text-sm font-mono">{rule.dont}</code>
                  </div>
                </div>
              </div>
            </SectionCard>
          ))}
        </div>
      </Section>

      {/* File Structure */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>File Structure</CardTitle>
              <CardDescription>Standard organization for component files</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/30 rounded-lg p-4 font-mono text-sm space-y-2">
            <div className="text-muted-foreground">registry/new-york/blocks/component-name/</div>
            {fileStructure.map((file) => (
              <div key={file.name} className="flex gap-4 pl-4">
                <div className="text-primary">{file.name}</div>
                <div className="text-muted-foreground">{`// ${file.description}`}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Naming Conventions */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Naming Conventions</CardTitle>
              <CardDescription>Consistent naming across the codebase</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Convention</th>
                <th className="text-left py-2 font-semibold">Example</th>
              </tr>
            </thead>
            <tbody>
              {namingConventions.map((item) => (
                <tr key={item.type} className="border-b">
                  <td className="py-2">{item.type}</td>
                  <td className="py-2">
                    <Badge variant="outline">{item.convention}</Badge>
                  </td>
                  <td className="py-2 font-mono text-xs text-muted-foreground">{item.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </Section>

      {/* Props Interface */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Props Interface Pattern</CardTitle>
              <CardDescription>Standard pattern for component props</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{`interface ComponentProps {
  // Required props first
  children: React.ReactNode;

  // Optional props with defaults
  variant?: 'solid' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';

  // State props (use 'is' prefix for booleans)
  isDisabled?: boolean;
  isLoading?: boolean;

  // Event handlers (use 'on' prefix)
  onChange?: (value: string) => void;
  onSelect?: (item: Item) => void;

  // Controlled/Uncontrolled pattern
  value?: string;           // Controlled
  defaultValue?: string;    // Uncontrolled

  // Style overrides last
  className?: string;
}`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Testing Requirements */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Testing Requirements</CardTitle>
              <CardDescription>Every component must include tests</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Render without crashing
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              All variants and sizes render correctly
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              User interactions work as expected
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Keyboard navigation functions properly
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              axe-core accessibility audit passes
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Controlled and uncontrolled modes work
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Edge cases handled (empty, loading, error states)
            </li>
          </ul>
        </SectionCard>
      </Section>
    </>
  );
}
